import { FetchResult } from '@apollo/client';
import { Button, Card, Col, DatePicker, Descriptions, Form, Input, message, Row, Space, theme, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useReducer, useState } from 'react';
import { APOLLO_DATE_FORMAT, DATE_FORMAT, FormatDate, IdCaseFileUploadSpec } from '../../../../../../constants';
import { IdentityVerificationCaseV1, CasesIdentityVerificationV1ApplicationDetailsContainerIdentityVerificationCaseV1UpdateMutation } from '../../../../../../generated';
import { Country } from '../../../../../../types/country';
import { AzureBlobResponse } from '../../../../../shared/file-upload/azure-upload';
import { CountryView } from '../../../../../shared/country-view/country-view';
import { CountryDropdownContainer } from '../../../../../shared/country-dropdown/country-dropdown-container';
import { IdCaseFileUploadButtonWithPreviewContainer } from './id-case-file-upload-button-with-preview.container';
import { Helmet } from 'react-helmet';

export interface ApplicationFormValueProps {
  caseName: string;
  passportCountry: string;
  passportExpiresAt: string;
  passportIssuedAt: string;
  passportNumber: string;
  purgeEligibleAt: Date;
  archiveEligibleAt: Date;
  expirationEligibleAt: Date;
}

const ApplicationFormAction = {
  EditCaseName: 'edit-case-name',
  SaveCaseName: 'save-case-name',
  CancelEditCaseName: 'cancel-edit-case-name',

  EditPassportDetails: 'edit-passport-details',
  SavePassportDetails: 'save-passport-details',
  CancelEditPassportDetails: 'cancel-edit-passport-details'
};

interface ApplicationDetailsProps {
  data: IdentityVerificationCaseV1;
  onSaveApplication: (data: ApplicationFormValueProps) => Promise<FetchResult<CasesIdentityVerificationV1ApplicationDetailsContainerIdentityVerificationCaseV1UpdateMutation>>;
  countryData: Country[];
  staffUserType: string;
  caseState: string;
  isSavingApplication: boolean;
  onUploadSuccess: (uploadedData: AzureBlobResponse, fieldName: string) => Promise<void>;
  isCurrentAssigned: boolean;
  isCaseOrApplicant: string;
}

interface ApplicationFormState {
  isPassportDetailsEditable: boolean;
  isCaseNameEditable: boolean;
  isDatesEditable: boolean;
}

export const ApplicationDetails: React.FC<ApplicationDetailsProps> = (props) => {
  const [isEditingPassportFiles, setIsEditingPassportFiles] = useState(false);
  const [isEditingPhotoFile, setIsEditingPhotoFile] = useState(false);

  const [caseNameForm] = Form.useForm<ApplicationFormValueProps>();
  const [overrideDetailsForm] = Form.useForm<ApplicationFormValueProps>();
  const [passportDetailsForm] = Form.useForm<ApplicationFormValueProps>();

  const [isSavingApplication, setIsSavingApplication] = useState(props.isSavingApplication);

  useEffect(() => {
    setIsSavingApplication(props.isSavingApplication);
    dispatch({ type: 'save-date-details' });
  }, [props.isSavingApplication]);
  const passportFiles = [
    {
      fileName: 'passport-expiration-page',
      fileLabel: 'Passport Expiration',
      fieldName: 'passportExpiration',
      buttonText: 'Upload Passport Expiration',
      permittedContentTypes: IdCaseFileUploadSpec.PassportExpirationPage.permittedContentTypes,
      permittedExtensions: IdCaseFileUploadSpec.PassportExpirationPage.permittedExtensions
    },
    {
      fileName: 'passport.jpg',
      fileLabel: 'Passport',
      fieldName: 'passport',
      buttonText: 'Upload Passport',
      permittedContentTypes: IdCaseFileUploadSpec.Passport.permittedContentTypes,
      permittedExtensions: IdCaseFileUploadSpec.Passport.permittedExtensions,
      cropperProps: { aspect: 3 / 4 }
    },
    {
      fileName: 'passport-translation.pdf',
      fileLabel: 'Passport Translation',
      fieldName: 'passportTranslation',
      buttonText: 'Upload Passport Translation',
      permittedContentTypes: IdCaseFileUploadSpec.PassportTranslation.permittedContentTypes,
      permittedExtensions: IdCaseFileUploadSpec.PassportTranslation.permittedExtensions
    }
  ];
  const { archiveEligibleAt, purgeEligibleAt, expirationEligibleAt, caseDetails } = props.data || {};
  const {
    token: { colorBgContainer, borderRadiusLG }
  } = theme.useToken();

  const initialStage: ApplicationFormState = {
    isPassportDetailsEditable: false,
    isCaseNameEditable: false,
    isDatesEditable: false
  };

  const disabled =
    !props.isCurrentAssigned || props.caseState === 'draft' || props.caseState === 'decisionRendered' || (props.caseState === 'revisionRequested' && props.isCurrentAssigned);

  const reducer = (state: ApplicationFormState, action: { type: string }) => {
    switch (action.type) {
      case 'save-edit-dates':
        return {
          ...state,
          isApplicationEditable: false,
          isCaseNameEditable: false,
          isDatesEditable: false
        };
      case 'cancel-edit-dates':
        return {
          ...state,
          isApplicationEditable: false,
          isCaseNameEditable: false,
          isDatesEditable: false
        };

      case 'edit-dates':
        return {
          ...state,
          isApplicationEditable: false,
          isCaseNameEditable: false,
          isDatesEditable: true
        };
      case ApplicationFormAction.EditCaseName:
        return {
          ...state,
          isCaseNameEditable: true,
          isDatesEditable: false
        };
      case ApplicationFormAction.EditPassportDetails:
        return {
          ...state,
          isDatesEditable: false,
          isPassportDetailsEditable: true
        };
      case ApplicationFormAction.CancelEditCaseName:
        setIsSavingApplication(false);
        return {
          ...state,
          isDatesEditable: false,
          isCaseNameEditable: false
        };
      case ApplicationFormAction.SaveCaseName:
        return {
          ...state,
          isDatesEditable: false,
          isCaseNameEditable: false
        };

      case ApplicationFormAction.CancelEditPassportDetails:
        setIsSavingApplication(false);
        return {
          ...state,
          isPassportDetailsEditable: false,
          isDatesEditable: false
        };
      case ApplicationFormAction.SavePassportDetails:
        return {
          ...state,
          isPassportDetailsEditable: false,
          isDatesEditable: false
        };
      case 'save':
        return {
          ...state,
          isPassportDetailsEditable: false,
          isCaseNameEditable: false,
          isDatesEditable: false
        };
      default:
        return state;
    }
  };

  const [applicationFormState, dispatch] = useReducer(reducer, initialStage);

  const labelStyle = {
    fontSize: '12px'
  };

  const labelTitleStyle = {
    fontSize: '15px',
    fontWeight: 400
  };

  const handleCancelEditCaseName = () => {
    dispatch({
      type: 'cancel-edit-case-name'
    });
    caseNameForm.resetFields();
  };

  const handleSaveCaseName = () => {
    caseNameForm
      .validateFields()
      .then(async (values) => {
        await handleSaveData(values, ApplicationFormAction.SaveCaseName);
      })
      .catch((errorInfo) => {
        console.log('Form Validation failed:', errorInfo);
      });
  };

  const handleCancelPassportDetails = () => {
    dispatch({
      type: 'cancel-edit-passport-details'
    });
    passportDetailsForm.resetFields();
  };

  const handleSavePassportDetails = () => {
    passportDetailsForm
      .validateFields()
      .then(async (values) => {
        values.passportIssuedAt = FormatDate(values.passportIssuedAt, APOLLO_DATE_FORMAT);
        values.passportExpiresAt = FormatDate(values.passportExpiresAt, APOLLO_DATE_FORMAT);

        await handleSaveData(values, ApplicationFormAction.SavePassportDetails);
      })
      .catch((errorInfo) => {
        console.log('Form Validation failed:', errorInfo);
      });
  };

  const handleSaveData = async (values: ApplicationFormValueProps, disPatchApplicationFormAction: string) => {
    try {
      const response = await props.onSaveApplication(values);
      if (response.data?.identityVerificationCaseV1Update.status.success) {
        message.success('Application data saved successfully');
        dispatch({ type: disPatchApplicationFormAction });
      } else {
        message.error("Error: Application data can't be saved in this case state");
      }
    } catch (error) {
      message.error('Error saving Application data');
    }
  };

  const handleOnUploadSuccess = async (uploadedData: AzureBlobResponse, fieldName: string) => {
    await props.onUploadSuccess(uploadedData, fieldName);
  };

  const handleSaveOverrideDetails = async (values: ApplicationFormValueProps) => {
    console.log(values);
    props.onSaveApplication(values).then(() => {
      message.success('Application data saved successfully');
      dispatch({ type: 'cancel-edit-dates' });
    });
  };

  const formatDate = (date: string | number | Date | dayjs.Dayjs | null | undefined) => FormatDate(date as string);
  const isEditingDisabled = () => disabled || Object.values(applicationFormState).some((value) => value) || isEditingPhotoFile || isEditingPassportFiles;

  return (
    <>
      <Helmet>
        <title>{props?.isCaseOrApplicant} - Application</title>
      </Helmet>
      <Card hoverable style={{ marginTop: '20px', padding: '24px' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              paddingLeft: 30,
              width: '100%'
            }}
          >
            {!applicationFormState.isCaseNameEditable && (
              <Descriptions
                extra={
                  <Button
                    type="primary"
                    onClick={() => {
                      dispatch({
                        type: 'edit-case-name'
                      });
                    }}
                    disabled={isEditingDisabled()}
                  >
                    Edit
                  </Button>
                }
                contentStyle={{ paddingLeft: 25 }}
                layout="vertical"
                title={<Typography.Title style={{ ...labelTitleStyle }}>Case Name</Typography.Title>}
              >
                <Descriptions.Item style={{ paddingBottom: '8px' }}>{props.data?.caseName || 'N/A'}</Descriptions.Item>
              </Descriptions>
            )}
            {/* Case name Edit */}
            {applicationFormState.isCaseNameEditable && (
              <Form
                form={caseNameForm}
                style={{ width: '100%' }}
                layout="vertical"
                initialValues={{
                  caseName: props.data?.caseName || 'N/A'
                }}
                onFinish={handleSaveCaseName}
              >
                <Row>
                  <Col flex="auto">
                    <Form.Item
                      label="Case Name"
                      name={['caseName']}
                      rules={[
                        {
                          required: true,
                          message: 'Please provide a case name'
                        }
                      ]}
                    >
                      <Input placeholder="Enter case name" style={{ width: 200 }} disabled={disabled} />
                    </Form.Item>
                  </Col>
                  <Col flex={'100px'} style={{ textAlign: 'right' }}>
                    <Space>
                      <Button onClick={handleCancelEditCaseName}>Cancel</Button>
                      <Button type="primary" htmlType="submit" loading={isSavingApplication} disabled={disabled}>
                        Save
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Form>
            )}

            <Typography.Text style={{ fontSize: '20px', fontWeight: 600, marginTop: '24px' }}>Applicant Provided Details</Typography.Text>
            <Typography.Text style={{ ...labelStyle, marginBottom: 36 }}>(Visible to Applicant)</Typography.Text>

            {/* Passport details View */}
            {!applicationFormState.isPassportDetailsEditable && (
              <>
                <CountryView
                  countryCode={props.countryData.find((country) => country.countryCode === caseDetails?.application?.passportDetails?.passportCountry)?.countryCode ?? ''}
                  extra={
                    <Button
                      type="primary"
                      onClick={() => {
                        dispatch({
                          type: 'edit-passport-details'
                        });
                      }}
                      disabled={isEditingDisabled()}
                    >
                      Edit
                    </Button>
                  }
                  layout="vertical"
                  title="Passport Details"
                  label="Passport Country"
                />
                <br />
                <Descriptions labelStyle={labelStyle} layout="vertical" column={3}>
                  <Descriptions.Item label="Passport Issue Date">{FormatDate(caseDetails?.application?.passportDetails?.passportIssuedAt)}</Descriptions.Item>
                  <Descriptions.Item label="Passport Expiration Date">{FormatDate(caseDetails?.application?.passportDetails?.passportExpiresAt)}</Descriptions.Item>
                  <Descriptions.Item label="Passport Number">{caseDetails?.application?.passportDetails?.passportNumber}</Descriptions.Item>
                </Descriptions>
              </>
            )}
          </div>
          {/* Passport details Edit */}
          {applicationFormState.isPassportDetailsEditable && (
            <Form
              form={passportDetailsForm}
              style={{ width: '100%', paddingLeft: 30 }}
              layout="vertical"
              initialValues={
                caseDetails?.application?.passportDetails || {
                  passportCountry: '',
                  passportExpiresAt: '',
                  passportIssuedAt: '',
                  passportNumber: ''
                }
              }
              onFinish={handleSavePassportDetails}
            >
              <Row>
                <Col flex="auto">
                  <Typography.Text style={{ marginTop: '24px' }}>Passport Details</Typography.Text>
                  <CountryDropdownContainer
                    style={{ width: 300 }}
                    label="Passport Country"
                    name={['passportCountry']}
                    rules={[
                      {
                        required: true,
                        message: 'Please select a passport country'
                      }
                    ]}
                    disabled={disabled}
                  />
                  <div style={{ display: 'flex', gap: 10 }}>
                    <Form.Item
                      name={['passportIssuedAt']}
                      valuePropName="value"
                      rules={[
                        {
                          required: true,
                          message: 'Passport issue date is required.'
                        }
                      ]}
                      label="Passport Issue Date"
                      getValueProps={(value) => ({
                        value: value ? dayjs(value) : ''
                      })}
                    >
                      <DatePicker
                        style={{ width: 150 }}
                        value={caseDetails?.application?.passportDetails?.passportIssuedAt ? formatDate(caseDetails?.application?.passportDetails.passportExpiresAt) : ''}
                        format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                        disabled={disabled}
                      />
                    </Form.Item>
                    <Form.Item
                      name={['passportExpiresAt']}
                      valuePropName="value"
                      rules={[
                        {
                          required: true,
                          message: 'Passport expiration date is required.'
                        }
                      ]}
                      label="Passport Expiration Date"
                      getValueProps={(value) => ({
                        value: value ? dayjs(value) : ''
                      })}
                    >
                      <DatePicker
                        style={{ width: 150 }}
                        value={
                          caseDetails?.application?.passportDetails?.passportExpiresAt
                            ? FormatDate(caseDetails?.application?.passportDetails?.passportExpiresAt)
                            : ''
                        }
                        format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                        disabled={disabled}
                      />
                    </Form.Item>
                  </div>
                  <Form.Item label="Passport Number" name={['passportNumber']} rules={[{ required: true, message: 'Passport number is required.' }]}>
                    <Input maxLength={9} minLength={9} style={{ width: 300 }} disabled={disabled} />
                  </Form.Item>
                </Col>
                <Col flex={'100px'} style={{ textAlign: 'right' }}>
                  <Space>
                    <Button onClick={handleCancelPassportDetails}>Cancel</Button>
                    <Button type="primary" htmlType="submit" loading={isSavingApplication} disabled={disabled}>
                      Save
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Form>
          )}
          <Descriptions
            column={3}
            labelStyle={labelStyle}
            layout="vertical"
            extra={
              <Button
                type={isEditingPassportFiles ? 'default' : 'primary'}
                onClick={() => {
                  if (isEditingPassportFiles) {
                    setIsEditingPassportFiles(false);
                  } else {
                    setIsEditingPassportFiles(true);
                  }
                }}
                disabled={isEditingDisabled() && !isEditingPassportFiles}
              >
                {isEditingPassportFiles ? 'Cancel' : 'Edit'}
              </Button>
            }
            style={{ paddingLeft: 30, paddingTop: 40 }}
          >
            {passportFiles.map((file) => {
              return (
                <Descriptions.Item label={file?.fileLabel ?? ''} key={file.fileName ?? ''}>
                  <IdCaseFileUploadButtonWithPreviewContainer
                    caseId={props.data?.id}
                    buttonText={'Upload/Reupload'}
                    permittedContentTypes={file.permittedContentTypes}
                    permittedExtensions={file.permittedExtensions}
                    fileName={file.fileName}
                    onUploadSuccess={async (response) => {
                      handleOnUploadSuccess(response, file.fieldName);
                      setIsEditingPassportFiles(false);
                    }}
                    isPreviewOnly={!isEditingPassportFiles}
                    isShowFileName={false}
                    cropperProps={file.cropperProps}
                  />
                </Descriptions.Item>
              );
            })}
          </Descriptions>
          <Descriptions
            title={'Photo Details'}
            layout="vertical"
            extra={
              <Button
                type={isEditingPhotoFile ? 'default' : 'primary'}
                onClick={() => {
                  if (isEditingPhotoFile) {
                    setIsEditingPhotoFile(false);
                  } else {
                    setIsEditingPhotoFile(true);
                  }
                }}
                disabled={isEditingDisabled() && !isEditingPhotoFile}
              >
                {isEditingPhotoFile ? 'Cancel' : 'Edit'}
              </Button>
            }
            style={{ paddingLeft: 30, paddingTop: 40 }}
          >
            <Descriptions.Item label="Photo" labelStyle={labelStyle}>
              <IdCaseFileUploadButtonWithPreviewContainer
                caseId={props.data?.id}
                buttonText={'Upload/Reupload'}
                permittedContentTypes={IdCaseFileUploadSpec.Photo.permittedContentTypes}
                permittedExtensions={IdCaseFileUploadSpec.Photo.permittedExtensions}
                fileName={IdCaseFileUploadSpec.Photo.fileName}
                onUploadSuccess={async (response) => {
                  handleOnUploadSuccess(response, IdCaseFileUploadSpec.Photo.fieldName);
                  setIsEditingPhotoFile(false);
                }}
                isPreviewOnly={!isEditingPhotoFile}
                isShowFileName={false}
                cropperProps={{ aspect: 1 }}
              />
            </Descriptions.Item>
          </Descriptions>

          {!applicationFormState.isDatesEditable && props?.staffUserType === 'Staff.IS' && (
            <>
              <br />
              <Descriptions
                title={<Typography.Text style={{ ...labelTitleStyle }}>Dates Details</Typography.Text>}
                layout="vertical"
                style={{ paddingLeft: 30 }}
                labelStyle={labelStyle}
                extra={
                  <Button
                    type="primary"
                    disabled={Object.values(applicationFormState).some((value) => value) || isEditingPhotoFile || isEditingPassportFiles}
                    onClick={() => {
                      dispatch({
                        type: 'edit-dates'
                      });
                    }}
                  >
                    Edit
                  </Button>
                }
              >
                <Descriptions.Item label="Purge Eligible At">{purgeEligibleAt ? FormatDate(purgeEligibleAt) : 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Archive Eligible At">{archiveEligibleAt ? FormatDate(archiveEligibleAt) : 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Expiration Eligible At">{expirationEligibleAt ? FormatDate(expirationEligibleAt) : 'N/A'}</Descriptions.Item>
              </Descriptions>
            </>
          )}

          {applicationFormState.isDatesEditable && (
            <div
              style={{
                width: '100%',
                paddingLeft: 32,
                borderRadius: borderRadiusLG,
                background: colorBgContainer,
                boxShadow: `0 2px 8px ${colorBgContainer}`
              }}
            >
              <Form
                form={overrideDetailsForm}
                style={{ width: '100%' }}
                layout="vertical"
                initialValues={{
                  purgeEligibleAt: purgeEligibleAt ? dayjs(purgeEligibleAt) : null,
                  archiveEligibleAt: archiveEligibleAt ? dayjs(archiveEligibleAt) : null,
                  expirationEligibleAt: expirationEligibleAt ? dayjs(expirationEligibleAt) : null
                }}
                onFinish={handleSaveOverrideDetails}
              >
                <Row>
                  <Descriptions
                    column={2}
                    layout="vertical"
                    extra={
                      <Col flex={'100px'} style={{ textAlign: 'right' }}>
                        <Space>
                          <Button
                            onClick={() => {
                              dispatch({
                                type: 'cancel-edit-dates'
                              });
                            }}
                          >
                            Cancel
                          </Button>
                          <Button type="primary" htmlType="submit" loading={isSavingApplication}>
                            Save
                          </Button>
                        </Space>
                      </Col>
                    }
                    title={<Typography.Text style={{ ...labelTitleStyle }}>Dates Details</Typography.Text>}
                    labelStyle={labelStyle}
                    style={{ marginTop: '24px' }}
                  />
                  <Col flex="auto">
                    <Form.Item label="Purge Eligible At" name={['purgeEligibleAt']}>
                      <DatePicker
                        style={{ width: 300 }}
                        value={purgeEligibleAt}
                        format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                        placeholder="Purge Eligible At"
                        disabled={props.caseState !== 'draft'}
                      />
                    </Form.Item>
                    <Form.Item label="Archive Eligible At" name={['archiveEligibleAt']}>
                      <DatePicker style={{ width: 300 }} placeholder="Archive Eligible At" format={(value) => value.format(DATE_FORMAT).toUpperCase()} />
                    </Form.Item>
                    <Form.Item label="Expiration Eligible At" name={['expirationEligibleAt']}>
                      <DatePicker
                        style={{ width: 300 }}
                        value={expirationEligibleAt}
                        placeholder="Expiration Eligible At"
                        format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </div>
          )}
        </div>
      </Card>
    </>
  );
};
